import { Container, Typography, Box } from "@mui/material";
import UnauthorizedBase from "../../../Base/views/UnauthorizedBase";

const PrivacyPolicyApp = () => {
  return (
    <UnauthorizedBase>
      <Box
        sx={{
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            py: 5,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Privacy Policy
          </Typography>
          <Box mt={4}>
            <Typography paragraph>
              This Privacy Policy explains how{" "}
              <strong>Talentstech Software Private Limited.</strong> ("Company", "we", "our",
              or "us") collects, uses, stores and protects information when you
              use the <strong>Meepl</strong> mobile application ("App"). The App
              is an employee-facing HRIS platform used for attendance, leave
              management and payroll services.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Who Uses This App</Typography>
            <Typography paragraph>
              Users of this App include employers ("Customers") and their
              authorized employees ("End Users"). The employer controls how the
              App is used and what data is collected according to company
              policies.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Information We Collect</Typography>

            <Typography variant="subtitle1" mt={2}>
              A. Personal & Employment Information
            </Typography>
            <Typography paragraph>
              We may collect employee name, email address, phone number, employee
              ID, department, designation and employment status for HRIS
              operations.
            </Typography>

            <Typography variant="subtitle1" mt={2}>
              B. Attendance & Working Hours Data
            </Typography>
            <Typography paragraph>
              We collect attendance check-in and check-out timestamps, working
              hours and leave records to calculate attendance and payroll.
            </Typography>

            <Typography variant="subtitle1" mt={2}>
              C. Location Information (Geo-Fencing)
            </Typography>
            <Typography paragraph>
              If enabled by your employer, the App may collect your location only
              at the time of marking attendance to verify geo-fenced work
              locations. Location data is not tracked continuously or in the
              background and is used solely for attendance verification, working
              hours calculation and payroll processing.
            </Typography>

            <Typography variant="subtitle1" mt={2}>
              D. Payroll Information
            </Typography>
            <Typography paragraph>
              Salary details, deductions, taxes and payslip records are processed
              and displayed within the App.
            </Typography>

            <Typography variant="subtitle1" mt={2}>
              E. Device & App Information
            </Typography>
            <Typography paragraph>
              We automatically collect device information such as operating
              system, app usage logs and crash reports to improve app performance
              and security.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">How We Use Your Information</Typography>
            <Typography paragraph>
              Information is used to authenticate users, record attendance, manage
              leaves, calculate working hours, generate payslips, improve system
              security and comply with legal obligations.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Data Ownership & Control</Typography>
            <Typography paragraph>
              All employee data is owned and controlled by the employer. We act as
              a data processor and process information only as instructed by the
              employer.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Data Storage & Security</Typography>
            <Typography paragraph>
              Data is stored on secure cloud infrastructure with encrypted data
              transmission. Access to personal data is restricted to authorized
              personnel only. While we implement industry-standard safeguards, no
              system can guarantee complete security.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Data Sharing</Typography>
            <Typography paragraph>
              We do not sell or rent personal information. Data may be shared only with authorized employer administrators, trusted service providers (including cloud hosting, analytics and infrastructure providers that support the operation of the App), or when required by applicable law or legal obligations.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Data Retention</Typography>
            <Typography paragraph>
              Data is retained only for as long as required to provide HRIS
              services and meet legal obligations. After service termination, data
              is deleted according to contractual agreements, except for
              anonymized or legally required records.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Permissions Used</Typography>
            <Typography paragraph>
              The App may request location access for geo-fenced attendance,
              storage access for downloading payslips, and internet access for
              syncing HRIS data. Permissions are used strictly for core
              functionality.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">User Rights</Typography>
            <Typography paragraph>
              Users may have the right to access, correct, or request deletion of their personal information, subject to employer policies and applicable laws.<br />
              Since the App is provided to organizations, such requests must be submitted to the respective employer (HR or administrator). We process such requests only based on instructions received from the authorized organization.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">Children’s Privacy</Typography>
            <Typography paragraph>
              This App is intended for users aged 18 years and above. We do not
              knowingly collect information from minors.
            </Typography>
          </Box>
          <Box mt={4}>
            <Typography variant="h5">
              Account Deletion & Data Removal
            </Typography>

            <Typography paragraph>
              Meepl is a Software-as-a-Service (SaaS) platform where all employee data is owned and controlled by the respective employer (organization). Meepl acts only as a data processor.
            </Typography>

            <Typography paragraph>
              End users (employees) cannot directly request account deletion from Meepl.
            </Typography>

            <Typography paragraph>
              If you wish to delete your account or personal data, you must contact your organization’s HR Admin and follow their internal process.
            </Typography>

            <Typography paragraph>
              Account deletion or data removal is performed only upon instructions from the authorized organization.
            </Typography>

            <Typography paragraph>
              Once processed by the organization:
            </Typography>

            <Typography component="ul" sx={{ pl: 3 }}>
              <li>
                <Typography variant="body1">
                  Your access to the application will be revoked
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  Your personal and account-related data will be deleted or anonymized, subject to system capabilities
                </Typography>
              </li>
            </Typography>

            <Typography paragraph sx={{ mt: 2 }}>
              Certain data may be retained for a limited period if required for legal, compliance, or organizational obligations.
            </Typography>
          </Box>

          <Box mt={4}>
            <Typography variant="h5">
              Changes to This Privacy Policy
            </Typography>
            <Typography paragraph>
              We may update this Privacy Policy from time to time. Updates will be
              posted on this page with a revised date.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Last Updated: 13 April 2026
          </Typography>
          <Box mt={4}>
            <Typography variant="h5">Contact Us</Typography>

            <Typography paragraph>
              For privacy-related questions or concerns regarding this Privacy Policy, you may contact us at:
            </Typography>

            <Typography paragraph sx={{ mt: -1.5 }}>
              Email:{" "}
              <Typography
                component="a"
                href="mailto:talenttechsuperuser@outlook.com"
                sx={{ color: "primary.main", textDecoration: "none" }}
              >
                talenttechsuperuser@outlook.com
              </Typography>
              <br />
              Website:{" "}
              <Typography
                component="a"
                href="https://meepl.day"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", textDecoration: "none" }}
              >
                https://meepl.day
              </Typography>
            </Typography>

            <Typography paragraph>
              For account deletion or data-related requests, end users must contact their employer or HR team.
            </Typography>
          </Box>
        </Container>
      </Box>
    </UnauthorizedBase>
  );
};

export default PrivacyPolicyApp;
